import type {
  ProductImageArea,
  ProductImageEntry,
  ProductImageScenario,
} from "./types";

interface Words {
  alt: string;
  shows: string;
  proves: string;
}

const entry = (
  id: string,
  module: string,
  opens: string,
  setup: readonly string[],
  sv: Words,
  en: Words,
  options: {
    area?: ProductImageArea;
    scenario?: ProductImageScenario;
    use?: readonly string[];
  } = {},
): ProductImageEntry => ({
  id,
  module,
  area: options.area ?? "solution",
  opens,
  setup,
  scenario: options.scenario ?? "S1",
  visual: { variant: 0 },
  text: { sv, en },
  use: options.use ?? ["pages/Plattform.tsx"],
});

/**
 * Surfaces that are new with the Brand 2.2 dashboard (2026-10-25): Rapporter
 * cards and Jämför (REPORT-UI-2), the Create step rail with Insatsflex and
 * Skiftflex chips, the eight-week horizon meter, the genomförandeplan slot
 * recommendation and the unused-hours pool. Captured on the Nacka curator
 * organisation; nothing reaches the website until the owner approves it.
 * Features the Platform Overview's Website edition does not list as Live
 * (slot recommendation, unused hours) are help-only.
 */
export const BRAND22_CATALOGUE: readonly ProductImageEntry[] = [
  entry(
    "reports.cards",
    "Rapporter",
    "Rapporter → rapportkort",
    [
      "Öppna Rapporter för enheten; schemat väljs automatiskt (närmaste utkast, annars det publicerade)",
    ],
    {
      alt: "Rapporter med rapportkort för enhetens schema",
      shows: "Välj en färdig rapport för schemat ni arbetar med.",
      proves:
        "Varje kort öppnar en färdig rapport för det valda schemat, utkast eller publicerat, utan att du väljer underlag först.",
    },
    {
      alt: "Reports with report cards for the unit's schedule",
      shows: "Pick a finished report for the schedule you are working on.",
      proves:
        "Each card opens a finished report for the selected schedule, draft or published, without choosing a source first.",
    },
    { use: ["pages/Plattform.tsx", "pages/Features/Analytics.tsx"] },
  ),
  entry(
    "reports.card.week",
    "Rapporter",
    "Rapporter → Veckorapport",
    ["Öppna Rapporter för enheten", "Välj Veckorapport"],
    {
      alt: "Veckorapporten med nyckeltal per dag och export till PDF och Excel",
      shows: "Läs veckans nyckeltal och ta ut rapporten som PDF eller Excel.",
      proves:
        "Rapporten visar vilket schema och vilken lösning den bygger på, och exporten gäller samma underlag.",
    },
    {
      alt: "The weekly report with daily key figures and PDF and Excel export",
      shows:
        "Read the week's key figures and export the report as PDF or Excel.",
      proves:
        "The report names the schedule and solution it is built from, and the export covers the same source.",
    },
    { use: ["pages/Plattform.tsx", "pages/Features/Analytics.tsx"] },
  ),
  entry(
    "reports.compare.proposals",
    "Rapporter · Jämför",
    "Rapporter → Jämför (utkast med två förslag)",
    [
      "Öppna Rapporter för ett utkast med två lösningsförslag",
      "Välj Jämför, som rekommenderas för utkastet",
    ],
    {
      alt: "Jämför två lösningsförslag i samma utkast med A, B och skillnaden",
      shows: "Jämför två förslag till samma schema innan ni väljer.",
      proves:
        "Varje mått visas för A och B med skillnaden B minus A, räknat var för sig ur respektive förslag.",
    },
    {
      alt: "Compare two solution proposals of one draft with A, B and the difference",
      shows: "Compare two proposals for the same schedule before choosing.",
      proves:
        "Every measure is shown for A and B with the difference B minus A, each counted from its own proposal.",
    },
    { use: ["pages/Plattform.tsx", "pages/Features/Analytics.tsx"] },
  ),
  entry(
    "planning.create-wizard.flex-choices",
    "Planering",
    "Översikt → Skapa nytt schema → Insatsflex och Skiftflex",
    ["Öppna Skapa nytt schema och välj enheten Nacka Syd och vecka 45"],
    {
      alt: "Stegen Insatsflex och Skiftflex i Skapa nytt schema med valda alternativ",
      shows: "Välj hur mycket besök och skift får flyttas när schemat skapas.",
      proves:
        "Varje steg visar det valda alternativet och vad det innebär, och sammanfattningen visar samma val innan något sparas.",
    },
    {
      alt: "The Visit flex and Shift flex steps in Create schedule with their chosen options",
      shows:
        "Choose how far visits and shifts may move when the schedule is created.",
      proves:
        "Each step shows the chosen option and what it means, and the summary shows the same choices before anything is saved.",
    },
    { area: "schedule" },
  ),
  entry(
    "planning.continuous.horizon-meter",
    "Planering",
    "Planering (löpande planering) → veckoremsan",
    [
      "Öppna Planering för Nacka Syd med löpande planering",
      "Läs hur många av de åtta veckorna framåt som är planerade",
    ],
    {
      alt: "Veckoremsan med mätaren för planerade veckor av åtta",
      shows: "Se hur många veckor framåt som redan har ett utkast.",
      proves:
        "Mätaren räknar veckorna i enhetens horisont som har ett planerat utkast.",
    },
    {
      alt: "The week strip with the meter of planned weeks out of eight",
      shows: "See how many weeks ahead already have a draft.",
      proves:
        "The meter counts the weeks in the unit's horizon that have a planned draft.",
    },
    { area: "schedule" },
  ),
  entry(
    "standing-data.care-plan-slot-recommendation",
    "Resurser · genomförandeplan",
    "Resurser → klient → Genomförandeplan → Redigera besök → Föreslå besökstider",
    [
      "Öppna ett besök i en klients genomförandeplan",
      "Läs de föreslagna tiderna; inget val sparas",
    ],
    {
      alt: "Föreslå besökstider med tider provade i enhetens planerade vecka",
      shows: "Få förslag på besökstider när ett besök läggs in eller ändras.",
      proves:
        "Förslagen är provade i enhetens planerade veckor med enhetens insatsflex; ett val fyller bara i formuläret.",
    },
    {
      alt: "Suggest visit times with times tried in the unit's planned week",
      shows: "Get suggested visit times when a visit is added or changed.",
      proves:
        "The suggestions are tried in the unit's planned weeks with its visit flex; choosing one only fills in the form.",
    },
    { area: "standing-data", scenario: "S5", use: ["help"] },
  ),
  entry(
    "standing-data.client-unused-hours",
    "Resurser · kommunbeslut",
    "Resurser → klient → Kommunbeslut → Outnyttjade timmar",
    ["Öppna Kommunbeslut för en klient med beviljade timmar"],
    {
      alt: "Outnyttjade timmar per beviljad pott för klientens månad",
      shows: "Se hur mycket av månadens beviljade tid som återstår.",
      proves:
        "Kortet visar beviljad, utförd och planerad tid per pott ur klientens beslut.",
    },
    {
      alt: "Unused hours per authorised pool for the client's month",
      shows: "See how much of the month's authorised time is left.",
      proves:
        "The card shows authorised, delivered and planned time per pool from the client's decision.",
    },
    { area: "standing-data", scenario: "S5", use: ["help"] },
  ),
];
